import styled, { keyframes, css } from "styled-components";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";

/* ═══════════════════════════════════════════════════════════════════════
   ReferralEarningsPage — Light Water redesign
   Frosted-white surfaces with aqua accents. Hero earnings card, monthly
   accordion sections, empty referral CTA, stat card breakdown, and a
   collapsible history table — all aligned to the design.md token set.
   All original named exports preserved so the JS layer stays untouched.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

/* ─── Shared mixins ─────────────────────────────────────────────────── */

const waterGlass = css`
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
`;

const frostedPill = css`
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
`;

const primaryPill = css`
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow: var(--shadow-cta);
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Layout ─── */

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 1160px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px 80px;

  @media (max-width: 768px) {
    padding: 0 12px 64px;
    gap: 14px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 32px 0 12px;

  @media (max-width: 768px) {
    padding: 22px 0 8px;
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: normal;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

export const BackButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  min-height: 36px;
  outline: none;
  cursor: pointer;
  ${frostedPill};
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }
`;

/* ─── Hero card ─── */

export const HeroCard = styled.div`
  position: relative;
  ${waterGlass};
  border-radius: 24px;
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Soft aqua bloom from top */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background: radial-gradient(
      120% 60% at 50% 0%,
      rgba(var(--color-brand-300-rgb), 0.32) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

export const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const HeroLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  svg {
    color: var(--color-brand-600);
  }
`;

export const HeroValue = styled.span`
  display: block;
  margin-top: 4px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

export const HeroDescription = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
`;

export const InviteButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 22px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  ${primaryPill};
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

/* ─── Info popup ─── */

export const Relative = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const PopupContainer = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%)
    translateY(${({ $isVisible }) => ($isVisible ? "0" : "-4px")});
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 240px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-3);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: var(--shadow-panel);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  transition:
    opacity 200ms var(--ease-snappy),
    transform 200ms var(--ease-snappy);

  /* Tiny pointer */
  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background: var(--color-surface-3);
    border-top: 1px solid var(--color-line);
    border-left: 1px solid var(--color-line);
  }
`;

export const PopupTitle = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
`;

export const PopupText = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`;

/* ─── Monthly sections accordion ─── */

export const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Chevron = styled(ChevronSvg)`
  width: 14px;
  height: 14px;
  transform: rotate(-90deg);
  transition: transform 280ms var(--ease-snappy);

  path {
    stroke: var(--color-brand-700);
  }
`;

export const Section = styled.div`
  ${waterGlass};
  border-radius: 18px;
  overflow: hidden;
  transition: border-color 220ms var(--ease-snappy);

  ${({ $open }) =>
    $open &&
    css`
      border-color: var(--color-line-strong);

      .chevron {
        transform: rotate(90deg);
      }
    `}
`;

export const DateLabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  transition: background 200ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-200-rgb), 0.18);
  }

  ${({ $open }) =>
    $open &&
    css`
      background: rgba(var(--color-brand-200-rgb), 0.22);
    `}
`;

export const DateLabel = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
`;

export const DateLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const EarningContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-brand-mist);
  border: 1px solid var(--color-line);
  color: var(--color-brand-700);

  [data-theme="dark"] & {
    border-color: transparent;
    background: rgba(var(--color-brand-400-rgb), 0.18);
    color: var(--color-text-strong);
  }
`;

export const Earning = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & {
    color: var(--color-text-strong);
  }
`;

export const LeaderboardContainer = styled.div`
  overflow: hidden;
  max-height: ${({ $open, $itemCount }) =>
    $open ? `${Math.max($itemCount || 1, 1) * 60 + 16}px` : "0"};
  transition: max-height 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

export const LeaderboardItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid var(--color-line);
`;

export const LeaderboardEmpty = styled.div`
  padding: 16px 18px;
  border-top: 1px solid var(--color-line);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
`;

export const LeaderboardName = styled.div`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

/* ─── Empty state ─── */

export const EmptyContainer = styled.div`
  position: relative;
  ${waterGlass};
  border-radius: 24px;
  padding: 40px 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Subtle aqua bloom */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background: radial-gradient(
      120% 60% at 50% 0%,
      rgba(var(--color-brand-300-rgb), 0.32) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 32px 20px;
  }
`;

export const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 420px;
  text-align: center;

  > svg {
    width: 38px;
    height: 38px;
    padding: 9px;
    border-radius: 14px;
    background: var(--color-brand-mist);
    border: 1px solid var(--color-line);
    color: var(--color-brand-700);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);

    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & > svg {
    border-color: transparent;
    background: var(--color-surface-3);
    color: var(--color-text-strong);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  }
`;

export const EmptyTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const EmptyTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;
`;

export const EmptySubtitle = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
`;

export const ReferralLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`;

export const ReferralLink = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-input);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);

  span {
    color: var(--color-brand-700);
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.005em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  svg {
    width: 16px;
    height: 16px;
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  ${({ $success }) =>
    $success &&
    css`
      color: #11a168;
      border-color: rgba(33, 189, 125, 0.45);
      background: rgba(33, 189, 125, 0.16);

      svg {
        color: #11a168;
      }
    `}
`;

export const LightText = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const ShareButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 240px;
  min-height: 46px;
  padding: 0 22px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  ${primaryPill};
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 56px 20px;
  ${waterGlass};
  border-radius: 22px;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
`;

/* ─── Stat cards ─── */

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
`;

/* ─── Withdraw summary ─── */

export const WithdrawCard = styled.div`
  ${waterGlass};
  border-radius: 22px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const WithdrawSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const WithdrawLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const WithdrawAmount = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: normal;
`;

export const WithdrawHint = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
`;

export const WithdrawButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 24px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  ${primaryPill};
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const StatUsdHint = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin-left: 6px;
`;

export const StatCard = styled.div`
  ${waterGlass};
  border-radius: 22px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StatCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding-bottom: 6px;

  > svg {
    min-width: 24px;
    min-height: 24px;
    flex-shrink: 0;
    padding: 4px;
    border-radius: 12px;
    background: var(--color-brand-mist);
    border: 1px solid var(--color-line);
    color: var(--color-brand-700);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);

    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & > svg {
    border-color: transparent;
    background: var(--color-surface-3);
    color: var(--color-text-strong);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4);
  }
`;

export const StatCardHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StatCardTitle = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.015em;
`;

export const StatCardSubtitle = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`;

export const StatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid var(--color-line);

  &:nth-of-type(2) {
    border-top: none;
  }
`;

export const StatLabel = styled.span`
  color: ${({ $dim }) =>
    $dim ? "var(--color-text-dim)" : "var(--color-text-muted)"};
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const StatValue = styled.span`
  color: ${({ $dim }) =>
    $dim ? "var(--color-text-muted)" : "var(--color-text-strong)"};
  font-family: var(--font-family-primary);
  font-size: ${({ $dim }) => ($dim ? "13px" : "16px")};
  font-weight: 800;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;
`;

/* ─── History section ─── */

export const HistorySection = styled.div`
  ${waterGlass};
  border-radius: 22px;
  padding: 14px 20px;
`;

export const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  padding: 4px 0;

  svg path {
    stroke: var(--color-brand-700);
  }
`;

export const HistoryTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.015em;
`;

export const HistoryList = styled.div`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? "1600px" : "0")};
  transition: max-height 360ms cubic-bezier(0.4, 0, 0.2, 1);
`;

export const HistoryTableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1.2fr 1fr 1fr;
  gap: 12px;
  padding: 14px 0 10px;
  margin-top: 6px;
  border-top: 1px solid var(--color-line);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1.2fr 1fr 1fr;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-line);
  align-items: center;

  &:last-of-type {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px 0;
  }
`;

export const HistoryCell = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: ${({ $bold }) => ($bold ? 800 : 500)};
  letter-spacing: -0.005em;
  text-transform: ${({ $capitalize }) => ($capitalize ? "capitalize" : "none")};

  ${({ $bold }) =>
    $bold &&
    css`
      color: var(--color-text-strong);
    `}

  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &::before {
      content: ${({ $label }) => ($label ? `"${$label}"` : "none")};
      color: var(--color-text-muted);
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
  }
`;

export const HistoryEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
`;

export const HistoryLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 0;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
`;

export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0 6px;
  flex-wrap: wrap;
`;

export const FooterText = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 14px;
  ${frostedPill};
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  transition:
    transform 180ms var(--ease-snappy),
    opacity 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const PaginationCurrent = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
`;

/* ─── Published templates table ─── */

export const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 14px 0;
  border-bottom: ${({ $isLast }) =>
    $isLast ? "none" : "1px solid var(--color-line)"};

  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileTemplateRow = styled.div`
  display: none;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 14px 0;
  border-bottom: ${({ $isLast }) =>
    $isLast ? "none" : "1px solid var(--color-line)"};

  @media (max-width: 768px) {
    display: flex;
  }
`;

export const TemplateColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  align-items: ${({ $alignItems }) => $alignItems || "flex-start"};
  gap: ${({ $gap }) => $gap || "4px"};
`;

export const TemplateLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const TemplateValue = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
